import '../serverSettings.scss'
import Input from 'components/FormComponents/Input/Input'
import Select from 'components/FormComponents/Select/Select'
import { InputTypes, SelectionTypes } from '../../../../lib/enums'
import Accordion from 'components/Accordion/Accordion'
import AccordionItem from 'components/Accordion/AccordionItem'
import Checkbox from 'components/Checkbox/Checkbox'
import Range from 'components/FormComponents/Range/Range'
import { useHuskyServerSettingsData } from './HuskyServerSettingsInitialization'
import { useEffect } from 'react'

const HuskyServerSettings = ({
  formData,
  serverSettings,
  camerasAmount,
  handleXProtectPlatformChange,
  handleValueChange,
  handleSupportedCamerasValueChange,
  hardwareTypeSelect,
  setHasManagementServerValidationError
}: HuskyServerSettingsProps) => {
  const {
    xProtectPlatforms,
    huskyXProtectVersion,
    huskyAddManagementServer,
    huskyXProtectVersionCameraMaxQty,
    ivoModels,
    huskyIvoModel,
    formFactors,
    huskyFormFactor,
    raidTypes,
    huskyRaidType,
    huskyMaxCamerasPerServer,
    huskyConcurrentSmartStreams,
    totalUsageSetup,
    huskyTotalUsage,
    handleHuskyTotalUsageChange,
    netStorageSetup,
    huskyNetStorage,
    serverDiskQuantities,
    huskyServerDiskQuantity,
    supportedCameras,
    huskySupportedCamera,
    handleHuskyNetStorageChange,
    handleHuskyXProtectPlatformChange,
    handleHuskyValueChange,
    handleHuskySupportedCamerasValueChange,
    isNumberOfCamerasExceedingManagementServerSelection
  } = useHuskyServerSettingsData(formData, serverSettings, camerasAmount, handleXProtectPlatformChange, handleValueChange, handleSupportedCamerasValueChange)

  useEffect(() => {
    setHasManagementServerValidationError?.(isNumberOfCamerasExceedingManagementServerSelection)
  }, [isNumberOfCamerasExceedingManagementServerSelection, setHasManagementServerValidationError])

  return (
    <>
      <div className="row align-items-center mt-6 mb-n8">
        {hardwareTypeSelect && hardwareTypeSelect()}
        <Select
          id="xsd__e2e-xprotect-version"
          selectionType={SelectionTypes.Number}
          className="col-3"
          defaultOption={{ label: 'Select XProtect Version', value: '' }}
          options={xProtectPlatforms}
          handleDataChange={handleHuskyXProtectPlatformChange()}
          initialValue={huskyXProtectVersion.toString()}
        />
        {/* <Checkbox className='col-2' id="failover-settings" label="Add Failover Server" handleChange={handleFailoverCheckboxChange} initialValue={isFailoverEnabled} isDisabled={!optionsSetup?.xProtectPlatform?.failoverSupport} /> */}
        <Checkbox
          className="col-3 server-settings-checkbox"
          id="xsd__e2e-add-management-server"
          label="Add Management Server"
          handleChange={handleHuskyValueChange(['huskyServerSettings', 'addManagementServer'])}
          initialValue={huskyAddManagementServer}
          isDisabled={huskyXProtectVersion === 3}
        />
        {huskyXProtectVersion === 3 && <div className="col-8 offset-3 mt-4 ml-1 msds-text-body-3 msds-text-gray-10">XProtect Express+ only supports up to 48 cameras</div>}
      </div>
      <Accordion>
        <AccordionItem title="Recording Server Settings" forceExpand>
          <div className="row">
            <Select
              id="xsd__e2e-husky-settings-husky-ivo-model"
              selectionType={SelectionTypes.Number}
              className="col-3 mb-4"
              defaultOption={{ label: 'Husky IVO Model', value: '1' }}
              options={ivoModels}
              isSmall
              handleDataChange={handleHuskyValueChange(['huskyServerSettings', 'ivoModel'])}
              initialValue={huskyIvoModel}
            />
            <Input
              id="xsd__e2e-husky-settings-max-cameras-per-server"
              type={InputTypes.Number}
              className="col-3 mt-7 mb-4"
              label="Max cameras per server"
              isSmall
              handleDataChange={handleHuskyValueChange(['huskyServerSettings', 'mainSettings', 'maxCamerasPerServer'], huskyMaxCamerasPerServer)}
              initialValue={huskyMaxCamerasPerServer.toString()}
              maximumNumber={huskyXProtectVersionCameraMaxQty}
              minimumNumber={1}
            />
            <div className="col-3 col-xl-2 mt-6">
              <Range
                id="xsd__e2e-husky-settings-total-usage"
                initialValue={huskyTotalUsage}
                min={totalUsageSetup.rangeMin}
                max={totalUsageSetup.rangeMax}
                inputName={totalUsageSetup.inputName}
                onChange={handleHuskyTotalUsageChange(['huskyServerSettings', 'maxDiskUtilization'])}
                minValue={totalUsageSetup.minValue}
              />
            </div>
            <div className="col-3 col-xl-2 offset-xl-1 mt-6">
              <Range
                id="xsd__e2e-husky-settings-net-storage"
                initialValue={huskyNetStorage}
                min={netStorageSetup.rangeMin}
                max={netStorageSetup.rangeMax}
                inputName={netStorageSetup.inputName}
                unitType={netStorageSetup.unitType}
                onChange={handleHuskyNetStorageChange(['huskyServerSettings', 'maxSystemUtilization'])}
                minValue={netStorageSetup.minValue}
              />
            </div>
            <Select
              id="xsd__e2e-husky-settings-form-factor"
              selectionType={SelectionTypes.Number}
              className="col-3 mb-4"
              defaultOption={{ label: 'Form factor', value: '1' }}
              options={formFactors}
              isSmall
              handleDataChange={handleHuskyValueChange(['huskyServerSettings', 'formFactor'])}
              initialValue={huskyFormFactor}
            />
            <Select
              id="xsd__e2e-husky-server-disk-quantity"
              selectionType={SelectionTypes.Number}
              className="col-3 mb-4"
              defaultOption={{ label: 'Server Disk Quantity', value: '' }}
              options={serverDiskQuantities}
              isSmall
              handleDataChange={handleHuskyValueChange(['huskyServerSettings', 'advancedSettings', 'serverDiskQuantity'])}
              initialValue={huskyServerDiskQuantity.toString()}
            />
            <Input
              id="xsd__e2e-husky-server-concurrent-remote-smart-client-streams-per-recording-server"
              type={InputTypes.Number}
              className="col-3 mt-7 mb-4"
              label="Remote smart client streams per server"
              isSmall
              handleDataChange={handleHuskyValueChange(['huskyServerSettings', 'mainSettings', 'concurrentSmartClientStreams'], parseInt(huskyConcurrentSmartStreams))}
              initialValue={huskyConcurrentSmartStreams}
              maximumNumber={huskyXProtectVersionCameraMaxQty}
              minimumNumber={1}
            />
            <Select
              id="xsd__e2e-husky-server-raid-os"
              selectionType={SelectionTypes.Number}
              className="col-3 mb-4"
              defaultOption={{ label: 'Raid Array', value: '' }}
              options={raidTypes}
              isSmall
              handleDataChange={handleHuskyValueChange(['huskyServerSettings', 'advancedSettings', 'liveDatabase', 'raidType'])}
              initialValue={huskyRaidType}
            />
          </div>
        </AccordionItem>
        <AccordionItem title="Management Server Settings" isDisabled={!huskyAddManagementServer} forceExpand={huskyAddManagementServer}>
          <div className="row justify-content-start mb-4">
            <Select
              id="xsd__e2e-Management-server-settings-supported-cameras"
              selectionType={SelectionTypes.Number}
              className="col-3"
              defaultOption={{ label: 'Supported Cameras', value: '' }}
              options={supportedCameras}
              isSmall
              handleDataChange={handleHuskySupportedCamerasValueChange()}
              initialValue={huskySupportedCamera.toString()}
            />
            {isNumberOfCamerasExceedingManagementServerSelection && (
              <div className="msds-text-body-3 xsd-proposals-pdf__card-info mt-2 msds-text-danger-red-1">Please note that the sum of carmeras you have added exceeds your selection.</div>
            )}
          </div>
        </AccordionItem>
      </Accordion>
    </>
  )
}

export default HuskyServerSettings

type HuskyServerSettingsProps = {
  formData
  serverSettings
  camerasAmount
  handleXProtectPlatformChange
  handleValueChange
  handleSupportedCamerasValueChange
  hardwareTypeSelect
  setHasManagementServerValidationError?: (hasError: boolean) => void
}
